import React, { useEffect, useRef, useState } from 'react'
import './new-reward-modal.styles.scss'

import Modal from '@/components/modal/modal.component'
import { useLocation, useNavigate } from 'react-router-dom'
import ImageContainer from '@/components/SHARED/image-container/image-container.component'
import { setNewStuffCount } from '@/state/slices/contentSlice'
import { properCase } from '@/utils/properCase'
import { UseCloudGameTransactions } from '@/utils/firebase.utils'
import { useDispatch, useSelector } from 'react-redux'

/**
 * Ordered list of newStuff matchers — first match wins.
 * Order matters: the more specific entry types must be tested before the
 * catch-all `display === 'reward'` matcher.
 */
const ALERT_MATCHERS = [
  {
    alertType: 'approvedItemWithReward',
    test: r => r.display === 'reward' && r.type === 'approvedItem',
  },
  {
    alertType: 'approvedItem',
    test: r => r.type === 'approvedItem',
  },
  {
    alertType: 'codeWithReward',
    test: r => r.display === 'reward' && r.type === 'secretCode',
  },
  {
    alertType: 'lootKeyWithReward',
    test: r => r.display === 'reward' && r.type === 'lootKey',
  },
  {
    // loot key that granted nothing — the backend leaves display as
    // 'playerActivity', so it never reaches the rewards page. Acknowledging it
    // here clears it from newStuff and reveals it on the Loot Keys page.
    alertType: 'lootKeyRedeemed',
    test: r => r.display === 'playerActivity' && r.type === 'lootKey',
  },
  {
    alertType: 'rewardWithReward',
    test: r => r.display === 'reward',
  },
  {
    alertType: 'achievementWithReward',
    test: r => r.display === 'achievement',
  },
  {
    alertType: 'prizepackWithReward',
    test: r => r.display === 'prizepack',
  },
  {
    alertType: 'purchaseComplete',
    test: r => r.display === 'purchase' && r.type === 'purchase',
  },
]

const NewRewardModal = ({
  gameData,
  gameElements,
  playerData,
}) => {

  const screenSize = useSelector(state => state.contentData.screenSize);
  const [show, setShow] = useState(null);
  const [alertType, setAlertType] = useState(null);
  const [modalImage, setModalImage] = useState(null);
  const [modalText, setModalText] = useState(null);
  // the newStuff entry currently driving the modal:
  // { alertType, activityId, element } — a fresh object per entry so the
  // display effect below re-fires even when two entries share an alertType
  const [pendingAlert, setPendingAlert] = useState(null);
  // ids the player has already been shown and dismissed — kept in a ref so
  // adding to it never re-runs the effect that reads it
  const dismissedIdsRef = useRef(new Set());
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    if (
      !playerData ||
      location.pathname.includes('/rewards')
    ) return;

    const newStuff = playerData.newStuff ?? [];

    // the badge count only tracks entries that actually render on the
    // rewards page — 'playerActivity' loot keys are deliberately excluded
    const rewardList = newStuff.filter(n =>
      n.display === 'reward' ||
      n.display === 'achievement' ||
      n.display === 'prizepack' ||
      n.display === 'purchase'
    );
    dispatch(setNewStuffCount(rewardList.length));

    const pending = newStuff.filter(n => !dismissedIdsRef.current.has(n.id));
    if (pending.length === 0) return;

    for (const matcher of ALERT_MATCHERS) {
      const activity = pending.find(matcher.test);
      if (!activity) continue;
      setPendingAlert(prev =>
        // keep the same object identity when nothing new arrived, otherwise
        // an unrelated playerData write would re-open a dismissed modal
        (prev && prev.activityId === activity.id)
          ? prev
          : {
            'alertType': matcher.alertType,
            'activityId': activity.id,
            'element': activity,
          }
      );
      return;
    }
  }, [playerData, location.pathname])

  useEffect(() => {
    if (!pendingAlert) return;
    const { alertType: type, element } = pendingAlert;
    setAlertType(type);

    if (
      type === 'approvedItem' ||
      type === 'approvedItemWithReward'
    ) {
      setModalImage(element ? element.icon : null);
      setModalText('An item you submitted has been ACCEPTED!');
    } else if (type === 'codeWithReward') {
      setModalImage(element ? element.icon : null);
      setModalText('You found a secret code!');
    } else if (
      type === 'lootKeyWithReward' ||
      type === 'lootKeyRedeemed'
    ) {
      const keyName = element && element.elementId && gameElements
        ? gameElements[element.elementId]?.name
        : null;
      setModalImage(
        (element && element.icon)
          ? element.icon
          : '/images/icons/lootkey.png'
      );
      setModalText(
        `You Redeemed a Loot Key!${
          keyName ? `<br />${properCase(keyName)}` : ''
        }${
          type === 'lootKeyWithReward'
            ? '<br />See what you unlocked.'
            : ''
        }`
      );
    } else if (type === 'rewardWithReward') {
      setModalImage('/images/icons/treasure2-cl.png');
      setModalText('You have New Rewards to Discover!');
    } else if (type === 'achievementWithReward') {
      setModalImage('/images/icons/achievement2_cl.png');
      setModalText('You have New Achievements!<br />Check out what you have earned.');
    } else if (type === 'prizepackWithReward') {
      setModalImage('/images/icons/prizepack2_cl.png');
      setModalText('You Have New Prize Packs to Open!');
    } else if (type === 'purchaseComplete') {
      setModalImage('/images/icons/shopping-cart.png');
      setModalText('Your Purchase is Complete!');
    } else {
      return;
    }
    setShow(true);

  }, [pendingAlert, gameElements])

  /**
   * closes the modal and records the entry so a later playerData write
   * cannot re-open it. alertType is intentionally left in place so the
   * button row does not flip while the modal animates out.
   */
  function dismissAlert() {
    if (pendingAlert) {
      dismissedIdsRef.current.add(pendingAlert.activityId);
    }
    setPendingAlert(null);
    setShow(null);
  }

  function goToRewards() {
    dismissAlert();
    navigate(
      location.pathname.includes('/preview/player/')
        ? `/preview/player/${gameData.path}/rewards`
        : `/play/${gameData.path}/rewards`
    );
    dispatch(setNewStuffCount(0));
  }

  async function handleOk() {
    const activityId = pendingAlert ? pendingAlert.activityId : null;
    if (
      activityId &&
      !location.pathname.includes('/preview/player/')
    ) {
      const data = {
        'gameId': gameData.gameId,
        'playerId': playerData.playerId,
        'rewardIdArr': [activityId]
      }
      const res = await UseCloudGameTransactions('markRewardAsViewed', data);
      console.log(res);
    }
    dismissAlert();
  }

  return (
    <div>
      <Modal
        show={show}
        cancel={() => dismissAlert()}
        cls={
          screenSize === 'mobile'
            ? 'full'
            : screenSize === 'tablet' ?
              '' :
              'narrow'
        }>
        <div className={`modal-children new-reward-modal ${screenSize}`}>
          <ImageContainer
            src={modalImage}
            alt='reward notification icon'
            className='treasure-gif' />
          <h2>
            <div dangerouslySetInnerHTML={{ __html: modalText }}></div>
          </h2>
          <hr />
          {
            (alertType && alertType.includes('WithReward'))
              ?
              <div className="button-div">
                <button
                  className={`g-button primary ${screenSize === 'mobile'
                      ? 'med-btn'
                      : ''
                    }`}
                  onClick={() => goToRewards()}>
                  Go to My Rewards!
                </button>
              </div>
              :
              <div className="buttons">
                <button
                  className={`g-button primary ${screenSize === 'mobile'
                      ? 'med-btn'
                      : ''
                    }`}
                  onClick={() => handleOk()}>
                  Ok
                </button>
              </div>
          }
        </div>
      </Modal>
    </div>
  )
}

export default NewRewardModal
